import React, { useState, useCallback, useEffect } from "react";
import clsx from "clsx";
import { Modal, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { reduxForm, Field, getFormValues, change } from "redux-form";

import { Button, CloseButton, CustomInput } from "components";
import { RequiredMarkContext } from "components/contexts";
import { getFullName } from "helpers";
import { useGetPreAppLinkMutation } from "queries";
import { dropdownValuesService, preAppService } from "services";
import { uiActions } from "store";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	emailValid,
	maxLength100,
	minLength2,
	normalizeAll,
	onlyAlphanumericAndCustomSpecialCharacters,
	required,
} from "validators/validation";

import SelectAsyncForPrimaryAgent from "../../select/selectAsyncForPrimaryAgent";
import UrlInfoIcon from "../../urlInfoIcon";

import { ReactComponent as CopyIcon } from "static/images/copy_icon.svg";
import styles from "./sendPreApp.module.scss";

const FORM_NAME = "sendPreApp";
const merchantName = "name";
const dbaName = "dba_name";
const defaultMerchantName = "Dear Merchant";

const SendPreAppModal = ({
	show,
	onHide,
	backdrop,
	dialogAs,
	showAgentSelect = true,
	defaultAgentId,
	defaultUrl,
	handleSubmit,
}) => {
	const dispatch = useDispatch();
	const [agents, setAgents] = useState();
	const [agentsLoading, setAgentsLoading] = useState(true);
	const [domain, setDomain] = useState();
	const [alias, setAlias] = useState();
	const [url, setUrl] = useState(defaultUrl);
	const [preselect, setPreselect] = useState({
		value: null,
		label: "Please Select",
	});

	useEffect(() => {
		if (!defaultUrl) {
			setUrl(`https://${domain || "[domain]"}/${alias || "[alias]"}`);
		}
	}, [defaultUrl, domain, alias]);

	const formValues = useSelector(getFormValues(FORM_NAME)) || {};

	const setDomainAlias = useCallback(agent => {
		const domainPart = agent ? agent.domain : null;
		const aliasPart = agent ? agent.alias : null;
		setDomain(domainPart);
		setAlias(aliasPart);
	}, []);

	useEffect(() => {
		const agent = agents?.find(a => a.id === formValues.agent_id);
		agent && setDomainAlias(agent);
	}, [formValues.agent_id, agents, setDomainAlias]);

	const loadAgents = useCallback(
		async (pattern, prevOptions, { page }) => {
			setAgentsLoading(true);
			const body = {
				pattern,
				page,
			};

			const {
				items: newAgents,
				count,
				currentUser,
			} = await dropdownValuesService.getPreAppPrimaryAgents(body);

			if (currentUser) {
				dispatch(change(FORM_NAME, "agent_id", currentUser.id));
				setPreselect({
					value: currentUser.id,
					label: getFullName(currentUser),
				});
				setDomainAlias(currentUser);
			}

			const hasMore = prevOptions.length + newAgents.length < count;

			setAgents(agents => {
				if (agents === undefined) {
					return newAgents;
				} else {
					return [...agents, ...newAgents];
				}
			});
			setAgentsLoading(false);

			return {
				options: newAgents.map(a => ({
					value: a.id,
					label: getFullName(a),
					alias: a.alias,
					domain: a.domain,
				})),
				hasMore,
				additional: {
					page: page + 1,
				},
			};
		},
		[dispatch, setDomainAlias],
	);

	const onSubmit = useCallback(
		formValues => {
			onHide();
			formValues[merchantName] =
				formValues[merchantName] || defaultMerchantName;
			formValues.agent_id = formValues.agent_id || defaultAgentId;
			preAppService
				.sendPreApp({ ...formValues, link: url })
				.then(() => {
					dispatch(
						uiActions.showInfoNotification({
							title: "Link was sent successfully",
						}),
					);
				})
				.catch(() => {
					dispatch(
						uiActions.showErrorNotification({
							title: "Couldn't send link",
						}),
					);
				});
		},
		[defaultAgentId, dispatch, onHide, url],
	);

	const hasAliasDomain = useCallback(
		agent_id => {
			if (agent_id) {
				const agent = agents?.find(a => a.id === agent_id);
				return (
					agent &&
					(!agent.alias || !agent.domain) &&
					"The agent has no alias or domain"
				);
			}
		},
		[agents],
	);

	const urlEnabled = domain && alias;

	const handleShowAgent = useCallback(
		agent_id => {
			if (!agent_id) return null;
			const agent = agents && agents.find(d => d.id === agent_id);

			return agent
				? { value: agent.id, label: getFullName(agent) }
				: null;
		},
		[agents],
	);

	const { mutate: getPreAppLink } = useGetPreAppLinkMutation();

	const handleCopyPreAppUrl = useCallback(() => {
		getPreAppLink(formValues.agent_id);
	}, [formValues.agent_id, getPreAppLink]);

	return (
		<Modal
			show={show}
			onHide={onHide}
			animation={false}
			centered
			dialogAs={dialogAs}
			backdrop={backdrop}
			size="lg"
		>
			<Modal.Header className={styles.header}>
				<Modal.Title className={styles.title}>
					Send&nbsp;
					<span className="text-capitalize">pre-application</span>
				</Modal.Title>
				<CloseButton onClick={onHide} />
			</Modal.Header>
			<Modal.Body className={styles.body}>
				<RequiredMarkContext.Provider value={true}>
					<form id="sendPreAppForm" onSubmit={handleSubmit(onSubmit)}>
						<Row>
							{showAgentSelect && (
								<Col
									md={{ span: 6, offset: 3 }}
									className={styles.urlContainer}
								>
									<a
										{...(urlEnabled && { href: url })}
										target="_blank"
										rel="noopener noreferrer"
										className={clsx(
											styles.url,
											"url-disabled",
										)}
									>
										{url}
									</a>

									<div className={styles.urlActions}>
										<Button
											onClick={handleCopyPreAppUrl}
											disabled={!urlEnabled}
											icon={<CopyIcon />}
											btnIcon
											variant="secondary"
											title="Copy Link"
											className="mr-2"
										/>
										<UrlInfoIcon width="16" alt="" />
									</div>
								</Col>
							)}
							<Col md={{ span: 6, offset: 3 }}>
								{showAgentSelect && (
									<Field
										component={SelectAsyncForPrimaryAgent}
										name="agent_id"
										loadOptions={loadAgents}
										findOption={handleShowAgent}
										validate={[required, hasAliasDomain]}
										defaultOptions={true}
										loading={agentsLoading}
										preselect={preselect}
									/>
								)}
								<Field
									component={CustomInput}
									label="Merchant Email"
									smLabel={12}
									smSelect={12}
									type="text"
									name="email"
									placeholder="Enter Merchant email"
									validate={[required, emailValid]}
									normalize={trimMidSentenceNormalizer}
									normalizeOnBlur={trimNormalizer}
								/>
								<Field
									component={CustomInput}
									label="Merchant Name"
									smLabel={12}
									smSelect={12}
									type="text"
									name={merchantName}
									placeholder="Enter Merchant Name"
									validate={[required, maxLength100]}
									normalize={normalizeAll([
										onlyAlphanumericAndCustomSpecialCharacters,
										trimMidSentenceNormalizer,
									])}
									normalizeOnBlur={trimNormalizer}
								/>
								<Field
									component={CustomInput}
									label="DBA Name"
									smLabel={12}
									smSelect={12}
									type="text"
									name={dbaName}
									placeholder="Enter DBA Name"
									validate={[
										required,
										minLength2,
										maxLength100,
									]}
									normalize={normalizeAll([
										onlyAlphanumericAndCustomSpecialCharacters,
										trimMidSentenceNormalizer,
									])}
									normalizeOnBlur={trimNormalizer}
								/>
							</Col>
						</Row>
					</form>
				</RequiredMarkContext.Provider>
			</Modal.Body>
			<Modal.Footer className={styles.footer}>
				<Button type="submit" form="sendPreAppForm" value="Send" />
			</Modal.Footer>
		</Modal>
	);
};

export default reduxForm({
	form: FORM_NAME,
})(SendPreAppModal);
